import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { User, LogOut } from 'lucide-react';
import { useAuthUser } from '@/hooks/useAuthUser';
import { useProviderAuth } from '@/hooks/useProviderAuth';

export function UserMenu() {
  const auth = useProviderAuth();
  const { user, profile } = useAuthUser();
  const navigate = useNavigate();

  if (!user) return null;

  // Use Supabase profile display name first, then fall back to Clerk data
  const displayName = profile?.display_name || user.fullName || user.firstName || user.username;
  // 🎯 Priority: Supabase avatar_url first, then fallback to Clerk
  const avatarUrl = profile?.avatar_url || user.imageUrl;
  const primaryEmail = user.primaryEmailAddress?.emailAddress;

  const handleSignOut = () => {
    auth.signOut(window.location.origin);
  };

  const handleProfileClick = () => {
    navigate('/profile');
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="flex items-center space-x-2 h-auto p-2">
          {/* Avatar */}
          <Avatar className="w-8 h-8">
            <AvatarImage src={avatarUrl} alt={displayName || "Avatar"} />
            <AvatarFallback>
              {displayName 
                ? displayName.split(' ').map(n => n[0]).join('').toUpperCase()
                : 'U'
              }
            </AvatarFallback>
          </Avatar>

          {/* Name or prompt */}
          {displayName ? (
            <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
              {displayName}
            </span>
          ) : (
            <span className="text-sm text-gray-500 dark:text-gray-400">
              Enter your name
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" side="bottom" className="w-64">
        {/* Header with avatar + name */}
        <div className="flex items-center p-4 border-b">
          <Avatar className="w-10 h-10">
            <AvatarImage src={avatarUrl} alt={displayName || "Avatar"} />
            <AvatarFallback>
              {displayName 
                ? displayName.split(' ').map(n => n[0]).join('').toUpperCase()
                : 'U'
              }
            </AvatarFallback>
          </Avatar>
          <div className="ml-3 min-w-0 flex-1">
            <div className="text-base font-semibold text-gray-900 dark:text-gray-100 truncate">
              {displayName || "Enter your name in profile"}
            </div>
            {primaryEmail && (
              <div className="text-sm text-gray-500 dark:text-gray-400 truncate">
                {primaryEmail}
              </div>
            )}
          </div>
        </div>

        {/* Menu Items */}
        <div className="py-2">
          <DropdownMenuItem onClick={handleProfileClick} className="cursor-pointer">
            <User className="mr-2 h-4 w-4" />
            <span>Profile</span>
          </DropdownMenuItem>
        </div>

        <DropdownMenuSeparator />
        
        <div className="py-2">
          <DropdownMenuItem onClick={handleSignOut} className="cursor-pointer text-red-600 dark:text-red-400">
            <LogOut className="mr-2 h-4 w-4" />
            <span>Sign out</span>
          </DropdownMenuItem>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
